import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";

const DestinationDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: destination, isLoading: loadingDest } = useQuery({
    queryKey: ["destination", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("destinations")
        .select("*")
        .eq("slug", slug!)
        .eq("is_active", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: tours = [], isLoading: loadingTours } = useQuery({
    queryKey: ["destination-tours", destination?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .eq("destination_id", destination!.id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!destination?.id,
  });

  if (loadingDest) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </main>
    );
  }

  if (!destination) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 gap-4">
          <p className="text-muted-foreground text-lg">Destination not found.</p>
          <Link to="/destinations"><Button>Back to Destinations</Button></Link>
        </div>
        <Footer />
      </main>
    );
  }

  const features = Array.isArray(destination.features) ? destination.features as string[] : [];
  const gallery = Array.isArray(destination.gallery) ? destination.gallery as string[] : [];

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={destination.main_image_url || "/placeholder.svg"} alt={destination.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[hsl(210,60%,10%)]/75" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-secondary/20 rounded-none px-4 py-2 mb-6 border border-secondary/30">
              <MapPin className="w-4 h-4 text-secondary" />
              <span className="text-secondary text-sm font-medium">Dominican Republic</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-4">{destination.title}</h1>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl">{destination.short_description}</p>
            {features.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {features.map((f: string, i: number) => (
                  <span key={i} className="bg-white/10 border border-white/20 rounded-none px-4 py-1.5 text-white text-sm">{f}</span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Description */}
      {destination.long_description && (
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="prose prose-lg max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: destination.long_description.replace(/\n/g, '<br/>') }} />
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="pb-16">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((img: string, i: number) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="rounded-none overflow-hidden aspect-square border border-border">
                  <img src={img} alt={`${destination.title} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tours */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
            Tours in <span className="text-secondary">{destination.title}</span>
          </h2>
          <p className="text-muted-foreground mb-8">{tours.length} tours available</p>

          {loadingTours ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : tours.length === 0 ? (
            <p className="text-muted-foreground text-center py-12">No tours available for this destination yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.map((tour: any, index: number) => (
                <motion.div
                  key={tour.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-card rounded-none overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-border hover:border-secondary/30"
                >
                  <Link to={`/tours/${tour.slug}`}>
                    <div className="relative h-52 overflow-hidden">
                      <img src={tour.main_image_url || "/placeholder.svg"} alt={tour.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(210,60%,10%)]/60 via-transparent to-transparent" />
                      {tour.is_featured && (
                        <div className="absolute top-3 left-3 bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1 rounded-none">Featured</div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-bold text-card-foreground mb-2 group-hover:text-primary transition-colors">{tour.title}</h3>
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{tour.short_description}</p>
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4">
                        <Clock className="w-4 h-4" /> {tour.duration || "Full Day"}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-muted-foreground text-xs">From</span>
                          <p className="text-xl font-bold text-secondary">${tour.adult_price || 0}</p>
                        </div>
                        <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-none text-xs px-4">View Details</Button>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default DestinationDetailPage;
